// Staff map editor view - wraps MapEditor component for adventure map editing

import { css, html } from 'lit';
import { api, getBackendUrl } from '../../lib/api.js';
import { BaseComponent } from '../../lib/base-component.js';
import { router } from '../../lib/router.js';
import '../editor/map-editor.js';

/**
 * @typedef {object} MapEditorChange
 * @property {string} id
 * @property {null} prevEventID
 * @property {number} timestamp
 * @property {string} eventType
 * @property {Array<{op: string, path: string, data?: *}>} changes
 * @property {string} [mapId]
 * @property {number} [gridSize]
 * @property {number} [gridOffsetX]
 * @property {number} [gridOffsetY]
 * @property {number} [gridRows]
 * @property {number} [gridCols]
 * @property {string} [globalIllumination]
 * @property {string} [backgroundColor]
 * @property {string} [backgroundImageUrl]
 * @property {object} [monsterPlacement]
 * @property {string} [monsterPlacementId]
 * @property {number} [toCol]
 * @property {number} [toRow]
 * @property {string} [direction]
 * @property {object} [updates]
 * @property {object} [wall]
 * @property {string} [wallId]
 * @property {object} [opening]
 * @property {string} [openingId]
 * @property {object} [light]
 * @property {string} [lightId]
 * @property {object} [cover]
 * @property {string} [coverId]
 * @property {Array<{col: number, row: number}>} [positions]
 */

/**
 * @typedef {object} StaffMonsterItem
 * @property {string} slug
 * @property {string} name
 * @property {string} cr
 * @property {string} creatureType
 * @property {string} [tokenSlug]
 * @property {string} [tokenImageUrl]
 * @property {string} [tokenSize]
 * @property {string} [tokenBorderColor]
 */

/**
 * @typedef {object} FetchMonstersParams
 * @property {number} page
 * @property {number} pageSize
 */

/**
 * @typedef {object} BackendActionResponse
 * @property {string} eventId
 * @property {string} eventType
 * @property {Array<{op: string, path: string, data?: *}>} changes
 */

/**
 * Staff map editor view component for editing adventure maps
 */
class StaffMapEditorView extends BaseComponent {
  static properties = {
    adventureSlug: { type: String, attribute: 'adventure-slug' },
    mapId: { type: String, attribute: 'map-id' },
    _adventureId: { type: String, state: true },
    _adventureName: { type: String, state: true },
    _mapIndex: { type: Number, state: true },
    _tool: { type: String, state: true },
    _panX: { type: Number, state: true },
    _panY: { type: Number, state: true },
    _zoom: { type: Number, state: true },
  };

  static styles = css`
    :host {
      display: block;
      height: 100%;
      overflow: hidden;
    }

    .editor-layout {
      display: flex;
      flex-direction: column;
      height: 100%;
    }

    .editor-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 0.5rem 1rem;
      background: #1a1a2e;
      border-bottom: 1px solid #333;
    }

    .editor-title {
      color: #e5e5e5;
      font-size: 0.9rem;
      font-weight: 500;
    }

    .map-editor-container {
      position: relative;
      flex: 1;
      min-height: 0;
    }
  `;

  constructor() {
    super();
    this.adventureSlug = '';
    this.mapId = '';
    this._adventureId = '';
    this._adventureName = '';
    this._mapIndex = 0;
    this._tool = 'select';
    this._panX = 0;
    this._panY = 0;
    this._zoom = 1.0;
  }

  /** @param {string} key */
  _getStorageKey(key) {
    return `d3-gamemap-editor-${this.mapId}-${key}`;
  }

  _loadViewportState() {
    const savedPanX = localStorage.getItem(this._getStorageKey('panX'));
    const savedPanY = localStorage.getItem(this._getStorageKey('panY'));
    const savedZoom = localStorage.getItem(this._getStorageKey('zoom'));

    if (savedPanX !== null) {
      this._panX = parseFloat(savedPanX);
    }
    if (savedPanY !== null) {
      this._panY = parseFloat(savedPanY);
    }
    if (savedZoom !== null) {
      this._zoom = parseFloat(savedZoom);
    }
  }

  _saveViewportState() {
    localStorage.setItem(this._getStorageKey('panX'), String(this._panX));
    localStorage.setItem(this._getStorageKey('panY'), String(this._panY));
    localStorage.setItem(this._getStorageKey('zoom'), String(this._zoom));
  }

  async connectedCallback() {
    super.connectedCallback();
    await this._loadMapInfo();
    this._loadViewportState();
  }

  async _loadMapInfo() {
    try {
      const adventure = await api.get(`/api/adventures/${this.adventureSlug}`);
      this._adventureId = adventure.id;
      this._adventureName = adventure.name;
      if (adventure.maps) {
        const idx = adventure.maps.findIndex((/** @type {{id: string}} */ m) => m.id === this.mapId);
        this._mapIndex = idx >= 0 ? idx : 0;
      }
    } catch (err) {
      console.error('Error loading map info:', err);
    }
  }

  /** @param {CustomEvent<string>} e */
  _handleToolChange(e) {
    this._tool = e.detail;
  }

  /** @param {CustomEvent<number>} e */
  _handlePanXChange(e) {
    this._panX = e.detail;
    this._saveViewportState();
  }

  /** @param {CustomEvent<number>} e */
  _handlePanYChange(e) {
    this._panY = e.detail;
    this._saveViewportState();
  }

  /** @param {CustomEvent<number>} e */
  _handleZoomChange(e) {
    this._zoom = e.detail;
    this._saveViewportState();
  }

  _goToAdventure() {
    router.navigate(`/staff/adventures/${this.adventureSlug}/edit`);
  }

  /** @param {{type: string, [key: string]: *}} action */
  async _saveAction(action) {
    const result = await api.post(`/api/adventures/${this.adventureSlug}/actions`, action);
    return this._processBackendResponse(result);
  }

  /** @param {BackendActionResponse} result */
  _processBackendResponse(result) {
    const { eventId, eventType, changes } = result;
    const firstChange = (changes || [])[0];
    const data = firstChange?.data;

    /** @type {MapEditorChange} */
    const event = {
      id: eventId,
      prevEventID: null,
      timestamp: Date.now(),
      eventType: eventType,
      changes: changes,
    };

    // Process based on event type
    switch (eventType) {
      case 'map:created':
        event.eventType = 'map:set';
        event.mapId = data?.id;
        event.gridSize = data?.gridSize || 70;
        event.gridOffsetX = data?.gridOffsetX || 0;
        event.gridOffsetY = data?.gridOffsetY || 0;
        event.gridRows = data?.gridRows || 0;
        event.gridCols = data?.gridCols || 0;
        event.globalIllumination = data?.globalIllumination || 'none';
        event.backgroundColor = '#000000';
        event.backgroundImageUrl = this._resolveImageUrl(data?.imageUrl) || '';
        break;

      case 'monsterPlacement:added':
        event.monsterPlacement = {
          id: data?.id,
          col: data?.col,
          row: data?.row,
          direction: data?.direction,
          size: data?.size,
          imageUrl: this._resolveImageUrl(data?.imageUrl),
          label: data?.label,
          name: data?.label,
          hidden: data?.hidden,
          placementType: data?.placementType,
          hp: data?.hp,
          maxHp: data?.maxHp,
          ac: data?.ac,
        };
        break;

      case 'monsterPlacement:updated':
        event.monsterPlacementId = data?.id;
        if (data?.col !== undefined) event.toCol = data.col;
        if (data?.row !== undefined) event.toRow = data.row;
        if (data?.direction !== undefined) event.direction = data.direction;
        event.updates = data;
        break;

      case 'monsterPlacement:moved':
        event.monsterPlacementId = data?.monsterPlacementId;
        event.toCol = data?.toCol;
        event.toRow = data?.toRow;
        break;

      case 'monsterPlacement:deleted':
        event.monsterPlacementId = data?.id;
        break;

      case 'wall:added':
        event.wall = { ...data, points: this._transformPoints(data?.points) };
        break;

      case 'wall:updated':
        event.wallId = data?.id;
        event.updates = data;
        break;

      case 'wall:deleted':
        event.wallId = data?.id;
        break;

      case 'opening:added':
        event.opening = { ...data, points: this._transformPoints(data?.points) };
        break;

      case 'opening:updated':
        event.openingId = data?.id;
        event.updates = data;
        break;

      case 'opening:deleted':
        event.openingId = data?.id;
        break;

      case 'light:added':
        event.light = data;
        break;

      case 'light:updated':
        event.lightId = data?.id;
        event.updates = data;
        break;

      case 'light:deleted':
        event.lightId = data?.id;
        break;

      case 'cover:added':
        event.cover = { ...data, points: this._transformPoints(data?.points) };
        break;

      case 'cover:updated':
        event.coverId = data?.id;
        event.updates = data;
        break;

      case 'cover:deleted':
        event.coverId = data?.id;
        break;

      case 'pc-starting-positions:set':
        event.positions = data;
        break;

      case 'map-settings:updated':
        event.globalIllumination = data;
        break;

      default:
        if (data) {
          Object.assign(event, data);
        }
    }

    return event;
  }

  /** @param {string | undefined} url */
  _resolveImageUrl(url) {
    if (url && !url.startsWith('http')) {
      return window.location.origin + url;
    }
    return url;
  }

  /** @param {Array<{x?: number, y?: number, col?: number, row?: number}> | undefined} points */
  _transformPoints(points) {
    if (!points) return points;
    return points.map(
      /** @param {{x?: number, y?: number, col?: number, row?: number}} p */ (p) =>
        p.x !== undefined ? p : { x: p.col, y: p.row },
    );
  }

  /** @param {FetchMonstersParams} params */
  async _fetchMonsters(params) {
    try {
      const data = /** @type {Array<StaffMonsterItem>} */ (await api.get('/api/staff/monsters'));

      // Apply pagination client-side
      const startIndex = params.page * params.pageSize;
      const endIndex = startIndex + params.pageSize;
      const paginatedMonsters = data.slice(startIndex, endIndex);

      return {
        monsters: paginatedMonsters.map(
          /** @param {StaffMonsterItem} m */ (m) => {
            let imageUrl = m.tokenImageUrl;
            if (imageUrl && !imageUrl.startsWith('http')) {
              imageUrl = window.location.origin + imageUrl;
            }

            return {
              slug: m.slug,
              name: m.name,
              cr: m.cr,
              creatureType: m.creatureType,
              tokenSlug: m.tokenSlug,
              tokenImageUrl: imageUrl ?? '',
              tokenSize: m.tokenSize ?? 'medium',
              tokenBorderColor: m.tokenBorderColor ?? '#000000',
            };
          },
        ),
        totalCount: data.length,
        hasMore: endIndex < data.length,
      };
    } catch (err) {
      console.error('Error fetching monsters:', err);
      throw err;
    }
  }

  render() {
    return html`
      <div class="editor-layout">
        <div class="editor-header">
          <span class="editor-title">${this._adventureName} Map ${this._mapIndex}</span>
          <button class="btn btn-outline-secondary btn-sm" @click="${this._goToAdventure}">Exit Editor</button>
        </div>

        <div class="map-editor-container">
          ${
            this._adventureId
              ? html`
                <map-editor
                  map-id="${this.mapId}"
                  adventure-id="${this._adventureId}"
                  backend-url="${getBackendUrl()}"
                  width="100%"
                  height="100%"
                  tool="${this._tool}"
                  pan-x="${this._panX}"
                  pan-y="${this._panY}"
                  zoom="${this._zoom}"
                  .saveAction="${
                    /** @param {{type: string, [key: string]: *}} action */ (action) => this._saveAction(action)
                  }"
                  .fetchMonsters="${/** @param {FetchMonstersParams} params */ (params) => this._fetchMonsters(params)}"
                  @tool-changed="${this._handleToolChange}"
                  @pan-x-changed="${this._handlePanXChange}"
                  @pan-y-changed="${this._handlePanYChange}"
                  @zoom-changed="${this._handleZoomChange}"
                ></map-editor>
              `
              : html`
                <div class="text-center py-4">
                  <div class="spinner-border">
                    <span class="visually-hidden">Loading...</span>
                  </div>
                </div>
              `
          }
        </div>
      </div>
    `;
  }
}

customElements.define('staff-map-editor-view', StaffMapEditorView);
